import moment from "moment";
import { defineStore } from "pinia";

import {
  APP_SUMO_MAX_USERS,
  APP_SUMO_TIER_TO_MAX_USERS,
  DEFAULT_ENTITLEMENTS,
  DEFAULT_TRIAL_TYPE,
  ORDERED_SUBSCRIPTIONS,
  SUBSCRIPTION_TO_INDEX,
} from "~/constants/billing";
import {
  Entitlement,
  GithubIntegrationStatus,
  NotionIntegrationStatus,
  ReleaseChannel,
  SamlConfigStatus,
  SlackIntegrationStatus,
  Subscription,
  UserRole,
  UserStatus,
} from "~/shared/enums";
import type { Tenant, TenantEntitlements } from "~/shared/types";

type State = Tenant & {
  _internalNow: string;
  sessionSubscriptionOverride: Subscription | null;
  sessionAppSumoTierOverride: number | null;
};

const GUESTS_PER_MEMBER = 3;

const useTenantStore = defineStore("TenantStore", {
  // TODO initializing all of these is a type hack that should be fixed
  state: (): State =>
    ({
      duid: "",
      isDart: false,
      name: "",
      createdAt: "",
      _internalNow: "",
      subscription: Subscription.PERSONAL,
      releaseChannel: ReleaseChannel.STANDARD,
      subscriptionDowngradeInitialized: false,
      everSubscribed: false,
      appSumoTier: null,
      entitlementOverrides: {},
      imageUrl: null,
      backlogEnabled: true,
      emailIntegrationEnabled: false,
      scimEnabled: false,
      closeParentOnCloseAllSubtasks: true,
      startParentOnStartAnySubtask: true,
      moveSubtasksOnMoveParent: true,
      updateSubtasksStatusOnUpdateParentStatus: false,
      copyParentFieldsOnCreate: true,
      updateBlockeeDatesOnUpdateBlockerDueDate: true,
      assignCreatorOnCreate: true,
      webhookSecret: "",
      workspaceDescription: null,
      enabledAiProviders: [],
      samlConfig: null,
      notionIntegration: null,
      slackIntegration: null,
      discordIntegration: null,
      githubIntegration: null,
      zapierIntegration: null,
      sessionSubscriptionOverride: null,
      sessionAppSumoTierOverride: null,
    }) satisfies State,
  getters: {
    assigneeEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultAssigneesProperty);
    },
    multipleAssigneesEnabled(): boolean {
      return this.$useDataStore().defaultAssigneesProperty.adtl.isMultiple;
    },
    tagsEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultTagsProperty);
    },
    priorityEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultPriorityProperty);
    },
    dueDateEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultDatesProperty);
    },
    startDateEnabled(): boolean {
      const datesProperty = this.$useDataStore().defaultDatesProperty;
      return this.$useAppStore().isPropertyShown(datesProperty) && datesProperty.adtl.isRange;
    },
    sizeEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultSizeProperty);
    },
    createdAtEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultCreatedAtProperty);
    },
    createdByEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultCreatedByProperty);
    },
    updatedAtEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultUpdatedAtProperty);
    },
    updatedByEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultUpdatedByProperty);
    },
    completedAtEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultCompletedAtProperty);
    },
    reviewerEnabled(): boolean {
      return this.$useAppStore().isPropertyShown(this.$useDataStore().defaultReviewersProperty);
    },
    multipleReviewersEnabled(): boolean {
      return this.$useDataStore().defaultReviewersProperty.adtl.isMultiple;
    },
    isTrialActive(): boolean {
      return (
        moment(this._internalNow).diff(moment(this.getEntitlementValue(Entitlement.TRIAL_START)), "days", true) <
        this.getEntitlementValue(Entitlement.TRIAL_DAYS)
      );
    },
    subscriptionNorm: (state) =>
      state.sessionSubscriptionOverride ??
      (state.isDart ? ORDERED_SUBSCRIPTIONS[ORDERED_SUBSCRIPTIONS.length - 1] : state.subscription),
    appSumoTierNorm: (state) => state.sessionAppSumoTierOverride ?? state.appSumoTier,
    subscriptionIncludingTrial(): Subscription {
      const subscription = this.subscriptionNorm;
      const trialType = this.getEntitlementValue(Entitlement.TRIAL_TYPE) as Subscription;
      if (this.isTrialActive && SUBSCRIPTION_TO_INDEX[trialType] > SUBSCRIPTION_TO_INDEX[subscription]) {
        return trialType;
      }
      return subscription;
    },
    userCount(): number {
      return this.$useDataStore()
        .getUserList({ excludeAgents: true, includeSpecialists: true })
        .filter((user) => user.status !== UserStatus.PENDING_SUBSCRIPTION_UPGRADE).length;
    },
    nonguestUserCount(): number {
      return this.$useDataStore()
        .getUserList({ excludeAgents: true, includeSpecialists: true })
        .filter((user) => user.status !== UserStatus.PENDING_SUBSCRIPTION_UPGRADE && user.role !== UserRole.GUEST)
        .length;
    },
    guestUserCount(): number {
      return this.$useDataStore()
        .getUserList({ excludeAgents: true, includeSpecialists: true })
        .filter((user) => user.status !== UserStatus.PENDING_SUBSCRIPTION_UPGRADE && user.role === UserRole.GUEST)
        .length;
    },
    billedUserCount(): number {
      return Math.max(this.nonguestUserCount, Math.ceil(this.guestUserCount / GUESTS_PER_MEMBER));
    },
    isSubscriptionGte() {
      return (subscription: Subscription, excludeTrial: boolean = false): boolean =>
        SUBSCRIPTION_TO_INDEX[!excludeTrial ? this.subscriptionIncludingTrial : this.subscriptionNorm] >=
        SUBSCRIPTION_TO_INDEX[subscription];
    },
    getEntitlementValue() {
      return <T extends Entitlement>(entitlement: T): TenantEntitlements[T] => {
        if (entitlement === Entitlement.TRIAL_DAYS) {
          return (this.entitlementOverrides[Entitlement.TRIAL_DAYS] ?? 0) as TenantEntitlements[T];
        }
        if (entitlement === Entitlement.TRIAL_START) {
          return (this.entitlementOverrides[Entitlement.TRIAL_START] ?? this.createdAt) as TenantEntitlements[T];
        }
        if (entitlement === Entitlement.TRIAL_TYPE) {
          return (this.entitlementOverrides[Entitlement.TRIAL_TYPE] ?? DEFAULT_TRIAL_TYPE) as TenantEntitlements[T];
        }

        let defaultValue: boolean | number = false;
        if (
          this.subscriptionNorm === Subscription.APP_SUMO &&
          (entitlement === Entitlement.MAX_USERS || entitlement === Entitlement.WEBHOOKS)
        ) {
          if (entitlement === Entitlement.MAX_USERS) {
            defaultValue = APP_SUMO_MAX_USERS;
            const tier = this.appSumoTierNorm;
            if (tier !== null && tier in APP_SUMO_TIER_TO_MAX_USERS) {
              defaultValue = APP_SUMO_TIER_TO_MAX_USERS[tier];
            }
          } else if (entitlement === Entitlement.WEBHOOKS) {
            defaultValue = (this.appSumoTierNorm ?? 0) >= 4;
          }
        } else {
          const subscriptionIndex = SUBSCRIPTION_TO_INDEX[this.subscriptionIncludingTrial];
          for (let i = subscriptionIndex; i >= 0; i -= 1) {
            const currSub = ORDERED_SUBSCRIPTIONS[i];
            if (entitlement in DEFAULT_ENTITLEMENTS[currSub]) {
              defaultValue = DEFAULT_ENTITLEMENTS[currSub][entitlement] as boolean | number;
              break;
            }
          }
        }

        if (!(entitlement in this.entitlementOverrides)) {
          return defaultValue as TenantEntitlements[T];
        }
        const overrideValue = this.entitlementOverrides[entitlement] as boolean | number;
        if (typeof defaultValue === "boolean") {
          return (defaultValue || overrideValue) as TenantEntitlements[T];
        }
        return Math.max(defaultValue, overrideValue as number) as TenantEntitlements[T];
      };
    },
    samlEnabled(): boolean {
      return this.samlConfig?.status === SamlConfigStatus.ENABLED;
    },
    githubIntegrationEnabled(): boolean {
      return this.githubIntegration?.status === GithubIntegrationStatus.ENABLED;
    },
    slackIntegrationEnabled(): boolean {
      return this.slackIntegration?.status === SlackIntegrationStatus.ENABLED;
    },
  },
  actions: {
    setTenant(tenant: Tenant) {
      this.duid = tenant.duid;
      this.isDart = tenant.isDart;
      this.name = tenant.name;
      this.createdAt = tenant.createdAt;
      this.subscription = tenant.subscription;
      this.releaseChannel = tenant.releaseChannel;
      this.subscriptionDowngradeInitialized = tenant.subscriptionDowngradeInitialized;
      this.everSubscribed = tenant.everSubscribed;
      this.appSumoTier = tenant.appSumoTier;
      this.entitlementOverrides = tenant.entitlementOverrides;
      this.imageUrl = tenant.imageUrl;
      this.backlogEnabled = tenant.backlogEnabled;
      this.emailIntegrationEnabled = tenant.emailIntegrationEnabled;
      this.scimEnabled = tenant.scimEnabled;
      this.closeParentOnCloseAllSubtasks = tenant.closeParentOnCloseAllSubtasks;
      this.startParentOnStartAnySubtask = tenant.startParentOnStartAnySubtask;
      this.moveSubtasksOnMoveParent = tenant.moveSubtasksOnMoveParent;
      this.updateSubtasksStatusOnUpdateParentStatus = tenant.updateSubtasksStatusOnUpdateParentStatus;
      this.copyParentFieldsOnCreate = tenant.copyParentFieldsOnCreate;
      this.updateBlockeeDatesOnUpdateBlockerDueDate = tenant.updateBlockeeDatesOnUpdateBlockerDueDate;
      this.assignCreatorOnCreate = tenant.assignCreatorOnCreate;
      this.webhookSecret = tenant.webhookSecret;
      this.enabledAiProviders = tenant.enabledAiProviders;
      this.samlConfig = tenant.samlConfig;
      this.notionIntegration = tenant.notionIntegration;
      this.workspaceDescription = tenant.workspaceDescription;
      if (this.notionIntegration) {
        this.notionIntegration.enabled = tenant.notionIntegration?.status === NotionIntegrationStatus.ENABLED;
      }
      this.slackIntegration = tenant.slackIntegration;
      this.discordIntegration = tenant.discordIntegration;
      this.githubIntegration = tenant.githubIntegration;
      this.zapierIntegration = tenant.zapierIntegration;

      this._internalNow = moment().toISOString();
    },
    updateClock() {
      this._internalNow = moment().toISOString();
    },
    setSessionSubscriptionOverride(subscription: Subscription | null) {
      this.sessionSubscriptionOverride = subscription;
    },
    setSessionAppSumoTierOverride(tier: number | null) {
      this.sessionAppSumoTierOverride = tier;
    },
    deleteTenant() {
      const userStore = this.$useUserStore();
      userStore.forceLogout();
    },
  },
});

export default useTenantStore;
